import { compact, keyBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadMultiSelectInput } from '@/components/form/baseInputs/TypeaheadMultiSelectInput/TypeaheadMultiSelectInput';
import { useFormContext } from '@/components/react-hook-form';

import { useDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import {
  buildCompositeAssetValues,
  distributeAssetOptionValues,
  useEntityAssetOptions,
} from '../hooks/useEntityAssetOptions';
import { SplitDispositionFormShape } from './SplitDispositionModal.types';

/**
 * Multi-select dropdown for choosing specific assets in the split disposition modal.
 * Uses the same asset options as the main recipient card's AssetSelectorDropdown
 * but writes to the SplitDispositionFormShape fields instead.
 */
export function SplitAssetSelector() {
  const { id: entityId } = useDispositiveProvisionsContext();
  const { control, setValue } = useFormContext<SplitDispositionFormShape>();
  const { options, loading } = useEntityAssetOptions(entityId);

  const [assetStaticIds, assetGroupIds, insurancePolicyIds, ownershipStakeIds] =
    useWatch({
      control,
      name: [
        'assetStaticIds',
        'assetGroupIds',
        'insurancePolicyIds',
        'ownershipStakeIds',
      ],
    });

  const compositeValues = useMemo(
    () =>
      buildCompositeAssetValues({
        assetStaticIds,
        assetGroupIds,
        insurancePolicyIds,
        ownershipStakeIds,
      }),
    [assetStaticIds, assetGroupIds, insurancePolicyIds, ownershipStakeIds]
  );

  const selectedOptions = useMemo(() => {
    const optionsByValue = keyBy(options, 'value');
    return compact(compositeValues.map((v) => optionsByValue[v]));
  }, [compositeValues, options]);

  const handleChange = useCallback(
    (_event: unknown, newValues: Array<{ value: string }>) => {
      const newCompositeValues = newValues.map((v) => v.value);
      const distributed = distributeAssetOptionValues(newCompositeValues);

      setValue('assetStaticIds', distributed.assetStaticIds, {
        shouldDirty: true,
      });
      setValue('assetGroupIds', distributed.assetGroupIds, {
        shouldDirty: true,
      });
      setValue('insurancePolicyIds', distributed.insurancePolicyIds, {
        shouldDirty: true,
      });
      setValue('ownershipStakeIds', distributed.ownershipStakeIds, {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const hasNoAssets = !loading && options.length === 0;
  const [inputValue, setInputValue] = useState('');

  return (
    <TypeaheadMultiSelectInput
      label="Assets"
      options={options}
      values={selectedOptions}
      onChange={handleChange}
      loading={loading}
      placeholder={hasNoAssets ? 'No assets available' : 'Select assets'}
      disabled={hasNoAssets}
      inputValue={inputValue}
      onInputChange={(value) => setInputValue(value)}
    />
  );
}
